/**
 * Input.tsx, Select.tsx, Textarea.tsx, SearchInput.tsx
 *
 * Form inputs with consistent styling. All support:
 *   - Focus state (cyan border glow)
 *   - Error state (red border + helper text)
 *   - Disabled state
 *   - Left/right icons or slots
 *   - Label + helper text
 *
 * Exports: Input, Select, Textarea, SearchInput, FormField
 */

import {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
  forwardRef,
  useState,
} from 'react';
import { theme } from '../../theme';
import { Icon } from './Icons';

const baseInputStyle: React.CSSProperties = {
  background: theme.surface.card,
  border: `1px solid ${theme.borderColor.default}`,
  color: theme.text.primary,
  padding: '8px 12px',
  borderRadius: theme.radius.md,
  fontSize: theme.fontSize.base,
  fontFamily: theme.font.ui,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  transition: `border-color ${theme.motion.fast}, background ${theme.motion.fast}`,
};

function getStateStyles(focus: boolean, error: boolean, disabled: boolean) {
  if (disabled) {
    return { borderColor: theme.borderColor.default, opacity: 0.5, cursor: 'not-allowed' as const };
  }
  if (error) {
    return { borderColor: theme.semantic.danger };
  }
  if (focus) {
    return { borderColor: theme.brand.cyan };
  }
  return {};
}

/* ═══════════════ FORM FIELD WRAPPER ═══════════════ */
/* Provides label, helper text, and error messaging around any input. */

interface FormFieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
}

export function FormField({ label, helper, error, required, children, style }: FormFieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      {label && (
        <label style={{
          fontSize: theme.fontSize.xs,
          fontWeight: theme.fontWeight.medium,
          letterSpacing: theme.letterSpacing.wide,
          textTransform: 'uppercase',
          color: theme.text.tertiary,
        }}>
          {label}
          {required && <span style={{ color: theme.semantic.danger, marginLeft: 4 }}>*</span>}
        </label>
      )}
      {children}
      {(helper || error) && (
        <div style={{
          fontSize: theme.fontSize.xs,
          color: error ? theme.semantic.danger : theme.text.tertiary,
          marginTop: 2,
        }}>
          {error || helper}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ INPUT ═══════════════ */

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: boolean;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
  mono?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { error, leftIcon, rightSlot, mono, disabled, style, onFocus, onBlur, ...rest },
  ref,
) {
  const [focus, setFocus] = useState(false);

  const pad = {
    paddingLeft: leftIcon ? 34 : 12,
    paddingRight: rightSlot ? 52 : 12,
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {leftIcon && (
        <span style={{
          position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          color: theme.text.tertiary,
          display: 'flex', alignItems: 'center',
          pointerEvents: 'none',
        }}>
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        disabled={disabled}
        onFocus={e => { setFocus(true); onFocus?.(e); }}
        onBlur={e => { setFocus(false); onBlur?.(e); }}
        style={{
          ...baseInputStyle,
          ...pad,
          fontFamily: mono ? theme.font.data : theme.font.ui,
          ...getStateStyles(focus, !!error, !!disabled),
          ...style,
        }}
        {...rest}
      />
      {rightSlot && (
        <span style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center',
        }}>
          {rightSlot}
        </span>
      )}
    </div>
  );
});

/* ═══════════════ SEARCH INPUT ═══════════════ */

interface SearchInputProps extends InputProps {
  kbd?: ReactNode;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { kbd = '⌘K', placeholder = 'Search…', ...rest },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="search"
      placeholder={placeholder}
      leftIcon={<Icon.Search size={14} />}
      rightSlot={kbd ? (
        <span style={{
          fontFamily: theme.font.data,
          fontSize: 10,
          color: theme.text.tertiary,
          background: theme.surface.base,
          border: `1px solid ${theme.borderColor.default}`,
          padding: '2px 5px',
          borderRadius: 4,
          pointerEvents: 'none',
        }}>
          {kbd}
        </span>
      ) : undefined}
      {...rest}
    />
  );
});

/* ═══════════════ SELECT ═══════════════ */

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { error, disabled, style, onFocus, onBlur, children, ...rest },
  ref,
) {
  const [focus, setFocus] = useState(false);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <select
        ref={ref}
        disabled={disabled}
        onFocus={e => { setFocus(true); onFocus?.(e); }}
        onBlur={e => { setFocus(false); onBlur?.(e); }}
        style={{
          ...baseInputStyle,
          paddingRight: 32,
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...getStateStyles(focus, !!error, !!disabled),
          ...style,
        }}
        {...rest}
      >
        {children}
      </select>
      <span style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        color: theme.text.tertiary,
        pointerEvents: 'none',
        display: 'flex', alignItems: 'center',
      }}>
        <Icon.ChevronDown size={14} />
      </span>
    </div>
  );
});

/* ═══════════════ TEXTAREA ═══════════════ */

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { error, mono, disabled, style, onFocus, onBlur, ...rest },
  ref,
) {
  const [focus, setFocus] = useState(false);

  return (
    <textarea
      ref={ref}
      disabled={disabled}
      onFocus={e => { setFocus(true); onFocus?.(e); }}
      onBlur={e => { setFocus(false); onBlur?.(e); }}
      style={{
        ...baseInputStyle,
        padding: '10px 12px',
        fontFamily: mono ? theme.font.data : theme.font.ui,
        resize: 'vertical',
        minHeight: 80,
        lineHeight: 1.5,
        ...getStateStyles(focus, !!error, !!disabled),
        ...style,
      }}
      {...rest}
    />
  );
});
